/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {FC, ReactNode, useEffect} from 'react';
import {withTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import Container from '@material-ui/core/Container';
import {useTheme} from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
// eslint-disable-next-line no-restricted-imports
import {useRouter} from 'next/router';
import LayoutContent, {LayoutContentProps} from '@ion/applications/src/layout/LayoutContent';
import {OfflineNotification} from '@ion/applications/src/prompts/OfflineNotification';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {SessionExpired} from '@/components/notifications/SessionExpired';
import {GlobalUserPrompt} from '@/components/prompts/GlobalUserPrompt';
import ResponsiveHeader from '@/header/ResponsiveHeader';
import {applyTranslations} from '@/security/Resellers';
import {login} from '@/store/actions/login';
import {ApplicationState} from '@/store/reducers';
import {isAuthenticatedSelector, usernameSelector} from '@/store/selectors/login-selectors';
import styles from './Layout.module.scss';

/**
 * Defines the top-level application Layout which includes the Side Navigation
 * and Header Component (Top Navigation). All Pages except the Login Page are
 * to be wrapped by the Layout Component.
 *
 * ```typescript
 *
 *  const Page = ({tReady}: LocalizedPageProps) => (
 *    <>
 *      <PageTitle namespace={'some-page'} initialized={tReady} />
 *      {tReady ? <SomePageComponent /> : <ContentLoader fullPage />}
 *    </>
 *  );
 *
 *  const SomePage = withTranslation([
 *    'some-page'
 *  ])(Page) as PageLayoutConsumer;
 *
 *  // implement getLayoutProps to override the default grid prop value ...
 *  SomePage.getLayoutProps = () => ({grid: true});
 *
 *  export default SomePage;
 *
 * ```
 */
const Layout: FC = ({children, grid = true, backgroundColor = dlsColor('white')}: LayoutContentProps) => {
  const theme              = useTheme();
  const route              = useRouter();
  const dispatch           = useDispatch();
  const username           = useSelector(usernameSelector);
  const isAuthenticated    = useSelector(isAuthenticatedSelector);
  const isInitialized      = useSelector(({initialization}: ApplicationState) => initialization.initializationComplete);
  const isPhone            = useMediaQuery(theme.breakpoints.down('sm'));
  const pageInitialized    = isAuthenticated && isInitialized;

  // when a page is initialized, if the user has not been authenticated, we redirect
  // to the login page ...
  useEffect(() => {
    !isAuthenticated && dispatch(login(null, null, route));
  }, [isAuthenticated, isInitialized]);

  return (
    <Container maxWidth='xs'>
      <SessionExpired />
      <ResponsiveHeader username={username} />
      <div className={styles[isPhone ? 'phone' : 'desktop']} style={{backgroundColor}}>
        <LayoutContent initialized={pageInitialized} grid={grid}>
          {children}
        </LayoutContent>
      </div>
      <OfflineNotification />
      <GlobalUserPrompt />
    </Container>
  );
};

export default withTranslation(
  applyTranslations([
    'common',
    'nav'
  ]))(React.memo(Layout));
